import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    MenuItem,
    IconButton,
    Chip,
    TableRow,
    TableCell,
    tableCellClasses,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import {
    getExpenses,
    getExpenseCategories,
    approveExpense,
    rejectExpense,
    deleteExpense,
    bulkApproveExpenses,
    bulkRejectExpenses,
} from '../../Redux/Actions/action';
import ErrorHandler from '../../Utils/handler';
import { toast } from 'react-toastify';
import CommonTable from '../../Components/Common/CommonTable';
import DataNotFound from '../../Components/Common/DataNotFound';
import CustomPagination from '../../Components/Common/CommonPagination';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import dayjs from 'dayjs';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme?.palette?.bgLightWhite?.main,
        fontSize: 14,
        textAlign: 'center',
        lineHeight: '1.2rem',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: 'center',
        padding: '10px 8px',
    },
}));

const ExpensesList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // State
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [summary, setSummary] = useState({});
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [filters, setFilters] = useState({
        status: '',
        category: '',
        fromDate: '',
        toDate: '',
        search: '',
    });
    const [rejectDialog, setRejectDialog] = useState({ open: false, expenseId: null, reason: '' });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, expenseId: null });

    const statusOptions = ['Draft', 'Pending', 'Approved', 'Rejected', 'Reimbursed', 'Cancelled'];
    const headerList = ['', 'No', 'Expense ID', 'Date', 'Title', 'Category', 'Amount', 'Paid To', 'Status', 'Created By', 'Actions'];

    // Fetch Data
    const fetchExpenses = async () => {
        dispatch(showLoader(true));
        try {
            const body = {
                page,
                limit: rowsPerPage,
                ...(filters.status && { status: filters.status }),
                ...(filters.category && { category: filters.category }),
                ...(filters.fromDate && { fromDate: filters.fromDate }),
                ...(filters.toDate && { toDate: filters.toDate }),
                ...(filters.search && { search: filters.search }),
            };

            const result = await dispatch(getExpenses(body));
            if (ErrorHandler(result)) {
                setExpenses(result?.payload?.data?.expenses || []);
                setTotalCount(result?.payload?.data?.totalCount || 0);
                setSummary(result?.payload?.data?.summary || {});
            }
        } catch (error) {
            console.error('Fetch expenses error:', error);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const fetchCategories = async () => {
        try {
            const result = await dispatch(getExpenseCategories({}));
            if (result?.payload?.success) {
                setCategories(result?.payload?.data || []);
            }
        } catch (error) {
            console.error('Fetch categories error:', error);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchExpenses();
    }, [page, rowsPerPage]);

    // Handlers
    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleApplyFilters = () => {
        setPage(1);
        fetchExpenses();
    };

    const handleResetFilters = () => {
        setFilters({ status: '', category: '', fromDate: '', toDate: '', search: '' });
        setPage(1);
        setTimeout(() => fetchExpenses(), 100);
    };

    const handleSelectExpense = (expenseId) => {
        setSelectedExpenses((prev) =>
            prev.includes(expenseId) ? prev.filter((id) => id !== expenseId) : [...prev, expenseId]
        );
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const pendingIds = expenses.filter((e) => e.status === 'Pending').map((e) => e._id);
            setSelectedExpenses(pendingIds);
        } else {
            setSelectedExpenses([]);
        }
    };

    const handleApprove = async (expenseId) => {
        dispatch(showLoader(true));
        try {
            const result = await dispatch(approveExpense({ id: expenseId }));
            if (ErrorHandler(result)) {
                toast.success('Expense approved successfully');
                fetchExpenses();
            }
        } catch (error) {
            toast.error('Failed to approve expense');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleReject = async () => {
        if (!rejectDialog.reason) {
            toast.error('Please provide a reason');
            return;
        }
        dispatch(showLoader(true));
        try {
            const result = await dispatch(rejectExpense({ id: rejectDialog.expenseId, reason: rejectDialog.reason }));
            if (ErrorHandler(result)) {
                toast.success('Expense rejected');
                setRejectDialog({ open: false, expenseId: null, reason: '' });
                fetchExpenses();
            }
        } catch (error) {
            toast.error('Failed to reject expense');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleDelete = async () => {
        dispatch(showLoader(true));
        try {
            const result = await dispatch(deleteExpense(deleteDialog.expenseId));
            if (ErrorHandler(result)) {
                toast.success('Expense deleted');
                setDeleteDialog({ open: false, expenseId: null });
                fetchExpenses();
            }
        } catch (error) {
            toast.error('Failed to delete expense');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleBulkApprove = async () => {
        if (selectedExpenses.length === 0) {
            toast.warning('Please select expenses to approve');
            return;
        }
        dispatch(showLoader(true));
        try {
            const result = await dispatch(bulkApproveExpenses({ ids: selectedExpenses }));
            if (ErrorHandler(result)) {
                toast.success(`${result?.payload?.data?.modifiedCount} expenses approved`);
                setSelectedExpenses([]);
                fetchExpenses();
            }
        } catch (error) {
            toast.error('Failed to bulk approve');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleBulkReject = async () => {
        const reason = prompt('Enter rejection reason:');
        if (!reason) return;
        
        dispatch(showLoader(true));
        try {
            const result = await dispatch(bulkRejectExpenses({ ids: selectedExpenses, reason }));
            if (ErrorHandler(result)) {
                toast.success(`${result?.payload?.data?.modifiedCount} expenses rejected`);
                setSelectedExpenses([]);
                fetchExpenses();
            }
        } catch (error) {
            toast.error('Failed to bulk reject');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const exportToExcel = async () => {
        dispatch(showLoader(true));
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Expenses');

            worksheet.columns = [
                { header: 'Expense ID', key: 'expenseId', width: 20 },
                { header: 'Date', key: 'date', width: 12 },
                { header: 'Title', key: 'title', width: 30 },
                { header: 'Category', key: 'category', width: 20 },
                { header: 'Amount', key: 'amount', width: 12 },
                { header: 'Paid To', key: 'paidTo', width: 20 },
                { header: 'Payment Mode', key: 'paymentMode', width: 15 },
                { header: 'Status', key: 'status', width: 12 },
                { header: 'Created By', key: 'createdBy', width: 20 },
            ];

            // Header styling
            worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
            worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '3E4094' } };

            expenses.forEach((exp) => {
                worksheet.addRow({
                    expenseId: exp.expenseId,
                    date: dayjs(exp.expenseDate).format('DD/MM/YYYY'),
                    title: exp.title,
                    category: exp.category?.name || '-',
                    amount: exp.amount,
                    paidTo: exp.paidTo,
                    paymentMode: exp.paymentMode,
                    status: exp.status,
                    createdBy: exp.createdByName || '-',
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Expenses_${dayjs().format('YYYY-MM-DD')}.xlsx`);
            toast.success('Exported successfully');
        } catch (error) {
            toast.error('Export failed');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            Draft: 'default',
            Pending: 'warning',
            Approved: 'success',
            Rejected: 'error',
            Reimbursed: 'info',
            Cancelled: 'default',
        };
        return colors[status] || 'default';
    };

    const renderTableRow = (expense, index) => {
        const rowNum = (page - 1) * rowsPerPage + index + 1;
        const isPending = expense.status === 'Pending';

        return (
            <TableRow key={expense._id} hover>
                <StyledTableCell>
                    <Checkbox
                        checked={selectedExpenses.includes(expense._id)}
                        onChange={() => handleSelectExpense(expense._id)}
                        disabled={!isPending}
                    />
                </StyledTableCell>
                <StyledTableCell>{rowNum}</StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 600, color: '#3E4094' }}>{expense.expenseId}</StyledTableCell>
                <StyledTableCell>{dayjs(expense.expenseDate).format('DD/MM/YYYY')}</StyledTableCell>
                <StyledTableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {expense.title}
                </StyledTableCell>
                <StyledTableCell>
                    <Chip
                        label={expense.category?.name || '-'}
                        size="small"
                        sx={{ backgroundColor: expense.category?.color + '20', color: expense.category?.color }}
                    />
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 600 }}>₹{expense.amount?.toLocaleString()}</StyledTableCell>
                <StyledTableCell>{expense.paidTo}</StyledTableCell>
                <StyledTableCell>
                    <Chip label={expense.status} color={getStatusColor(expense.status)} size="small" />
                </StyledTableCell>
                <StyledTableCell>{expense.createdByName || '-'}</StyledTableCell>
                <StyledTableCell>
                    <Box display="flex" justifyContent="center" gap={0.5}>
                        <Tooltip title="View">
                            <IconButton size="small" onClick={() => navigate(`/expenses/view/${expense._id}`)}>
                                <VisibilityIcon fontSize="small" color="primary" />
                            </IconButton>
                        </Tooltip>
                        {['Draft', 'Pending'].includes(expense.status) && (
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => navigate(`/expenses/edit/${expense._id}`)}>
                                    <EditIcon fontSize="small" color="info" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {isPending && (
                            <>
                                <Tooltip title="Approve">
                                    <IconButton size="small" onClick={() => handleApprove(expense._id)}>
                                        <CheckCircleIcon fontSize="small" color="success" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                    <IconButton
                                        size="small"
                                        onClick={() => setRejectDialog({ open: true, expenseId: expense._id, reason: '' })}
                                    >
                                        <CancelIcon fontSize="small" color="error" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        {expense.status === 'Draft' && (
                            <Tooltip title="Delete">
                                <IconButton
                                    size="small"
                                    onClick={() => setDeleteDialog({ open: true, expenseId: expense._id })}
                                >
                                    <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </StyledTableCell>
            </TableRow>
        );
    };

    return (
        <Box>
            <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px' }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={600}>
                        Expenses Management
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportToExcel}>
                            Export
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/expenses/add')}
                        >
                            Add Expense
                        </Button>
                    </Box>
                </Box>

                {/* Summary Cards */}
                <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                    <Paper sx={{ p: 2, flex: 1, minWidth: 150, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="body2" color="textSecondary">Total</Typography>
                        <Typography variant="h6" fontWeight={600}>₹{summary.totalAmount?.toLocaleString() || 0}</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, flex: 1, minWidth: 150, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                        <Typography variant="body2" color="textSecondary">Approved</Typography>
                        <Typography variant="h6" fontWeight={600} color="success.main">
                            ₹{summary.approvedAmount?.toLocaleString() || 0}
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, flex: 1, minWidth: 150, bgcolor: '#fff3e0', borderRadius: 2 }}>
                        <Typography variant="body2" color="textSecondary">Pending</Typography>
                        <Typography variant="h6" fontWeight={600} color="warning.main">
                            ₹{summary.pendingAmount?.toLocaleString() || 0}
                        </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, flex: 1, minWidth: 150, bgcolor: '#ffebee', borderRadius: 2 }}>
                        <Typography variant="body2" color="textSecondary">Rejected</Typography>
                        <Typography variant="h6" fontWeight={600} color="error.main">
                            ₹{summary.rejectedAmount?.toLocaleString() || 0}
                        </Typography>
                    </Paper>
                </Box>

                {/* Filters */}
                <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9', borderRadius: '12px' }}>
                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                        <FilterListIcon color="primary" />
                        <TextField
                            select
                            label="Status"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            size="small"
                            sx={{ minWidth: 120 }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {statusOptions.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Category"
                            value={filters.category}
                            onChange={(e) => handleFilterChange('category', e.target.value)}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            type="date"
                            label="From Date"
                            value={filters.fromDate}
                            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            label="To Date"
                            value={filters.toDate}
                            onChange={(e) => handleFilterChange('toDate', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Search"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            size="small"
                            placeholder="ID, Title, Paid To..."
                            sx={{ minWidth: 180 }}
                        />
                        <Button variant="contained" onClick={handleApplyFilters} size="small">
                            Apply
                        </Button>
                        <IconButton onClick={handleResetFilters} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Box>
                </Paper>

                {/* Bulk Actions */}
                {selectedExpenses.length > 0 && (
                    <Box display="flex" gap={2} mb={2} alignItems="center">
                        <Typography variant="body2">{selectedExpenses.length} selected</Typography>
                        <Button variant="contained" color="success" size="small" onClick={handleBulkApprove}>
                            Approve Selected
                        </Button>
                        <Button variant="outlined" color="error" size="small" onClick={handleBulkReject}>
                            Reject Selected
                        </Button>
                    </Box>
                )}

                {/* Table */}
                <CommonTable headerList={headerList} minWidth={1200}>
                    {expenses.length > 0 ? (
                        expenses.map((exp, idx) => renderTableRow(exp, idx))
                    ) : (
                        <TableRow>
                            <StyledTableCell colSpan={headerList.length}>
                                <DataNotFound
                                    title="No Expenses Found"
                                    icon={<ErrorOutlineIcon color="primary" style={{ fontSize: '3rem' }} />}
                                    elevation={0}
                                />
                            </StyledTableCell>
                        </TableRow>
                    )}
                </CommonTable>

                {/* Pagination */}
                {totalCount > 0 && (
                    <Box mt={2}>
                        <CustomPagination
                            count={totalCount}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(1);
                            }}
                        />
                    </Box>
                )}
            </Paper>

            {/* Reject Dialog */}
            <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, expenseId: null, reason: '' })}>
                <DialogTitle>Reject Expense</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Rejection Reason"
                        fullWidth
                        multiline
                        rows={3}
                        value={rejectDialog.reason}
                        onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialog({ open: false, expenseId: null, reason: '' })}>Cancel</Button>
                    <Button onClick={handleReject} color="error" variant="contained">Reject</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, expenseId: null })}>
                <DialogTitle>Delete Expense</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this expense?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, expenseId: null })}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExpensesList;
